'use client';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Content,
  ImageFieldImage,
  LinkField,
  RichTextField,
} from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';

interface StepType {
  step_row_name: string | null;
  icon: ImageFieldImage | null | undefined;
  step_title: RichTextField | null | undefined;
  step_description: RichTextField | null | undefined;
  button_label?: string | null;
  button_link?: LinkField | null | undefined;
}

interface StepWithIndexProps {
  step: StepType;
  index: number;
}

interface StepTitleProps {
  step_row_name: string | null;
  index: number;
}

/**
 * Props for `ProcessStep`.
 */
export type ProcessStepProps = SliceComponentProps<Content.ProcessStepSlice>;

const StepTitle = ({ step_row_name, index }: StepTitleProps) => (
  <p className="text-h3">{step_row_name ?? `Step ${index + 1}`}</p>
);

const MobileStep = ({ step, index }: StepWithIndexProps) => (
  <div className="md:hidden grid grid-cols-[16px_1fr] gap-4">
    <div className="relative flex flex-col items-center">
      <div className="w-[16px] h-[16px] bg-gradient-to-b from-primary-darker-green to-primary-shade-green rounded-full mt-[-8px] mb-2"></div>
      <div className="w-[3px] bg-gradient-to-b from-primary-darker-green to-primary-shade-green h-full min-h-[64px]"></div>
    </div>
    <div className="relative pt-8">
      <div className="absolute -top-6 -left-0 text-primary-darker-green">
        <StepTitle step_row_name={step.step_row_name} index={index} />
      </div>
      <div className="mb-3">
        <PrismicNextImage field={step.icon}></PrismicNextImage>
      </div>
      <div className="pb-6">
        <PrismicRichText field={step.step_title} />
      </div>
      <div className="mb-10">
        <PrismicRichText field={step.step_description} />
      </div>
      {step.button_label && (
        <div className="flex mb-4">
          <PrismicNextLink field={step.button_link} className="button-bordered">
            <span>{step.button_label}</span>
          </PrismicNextLink>
        </div>
      )}
    </div>
  </div>
);

const DesktopStep = ({ step, index }: StepWithIndexProps) => (
  <div className="hidden md:grid md:grid-cols-[150px_50px_6fr] items-start gap-4">
    <div className="text-primary-darker-green h-full">
      <StepTitle step_row_name={step.step_row_name} index={index} />
    </div>
    <div className="flex flex-col items-center h-full pt-4">
      <div className="mb-5">
        <div className="w-[16px] h-[16px] bg-gradient-to-b from-primary-darker-green to-primary-shade-green rounded-full"></div>
      </div>
      <div className="w-[3px] bg-gradient-to-b from-primary-darker-green to-primary-shade-green h-full"></div>
    </div>
    <div className="flex flex-col relative pb-16">
      <div className="absolute -top-12 -left-0">
        <PrismicNextImage field={step.icon}></PrismicNextImage>
      </div>
      <div className="pb-6">
        <PrismicRichText field={step.step_title} />
      </div>
      <div className="mb-10">
        <PrismicRichText field={step.step_description} />
      </div>
      {step.button_label && (
        <div className="flex">
          <PrismicNextLink field={step.button_link} className="button-bordered">
            <span>{step.button_label}</span>
          </PrismicNextLink>
        </div>
      )}
    </div>
  </div>
);

/**
 * Component for "ProcessStep" Slices.
 */
const ProcessStep = ({ slice }: ProcessStepProps): JSX.Element => {
  const {
    primary: { title, steps },
  } = slice;

  const isScrollable = steps.length > 3;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);

  const handleScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      setShowTopGradient(scrollTop > 0);
      setShowBottomGradient(scrollTop + clientHeight < scrollHeight - 20);
    }
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="text-white max-w-screen-2xl mx-auto px-5 md:px-16"
    >
      <div className="text-left lg:text-center mt-5 mb-10">
        <PrismicRichText field={title} />
      </div>

      <div className="relative">
        {isScrollable && showTopGradient && (
          <div className="absolute -top-1 left-0 right-0 h-8 gradient-mask-t-[transparent,rgba(0,0,0,1.0)_30px,rgba(0,0,0,0.5)_40%] pointer-events-none bg-black bg-opacity-50 rounded-t-xl"></div>
        )}

        <div
          ref={scrollContainerRef}
          className={`${isScrollable ? 'max-h-[400px] overflow-y-auto scrollbar-hide relative' : ''}`}
        >
          {/* Desktop layout offset top line*/}
          <div className="hidden md:grid grid-cols-[150px_50px_6fr] items-start gap-4">
            <div className="text-primary-darker-green h-full"></div>
            <div className="flex flex-col items-center h-full pt-4">
              <div className="w-[3px] bg-gradient-to-b from-primary-darker-green to-primary-shade-green h-full min-h-[64px]"></div>
            </div>
            <div className="flex flex-col"></div>
          </div>

          {/* Mobile layout offset top line*/}
          <div className="md:hidden grid grid-cols-[16px_1fr] gap-4 mb-4">
            <div className="flex flex-col items-center">
              <div className="w-[3px] bg-gradient-to-b from-primary-darker-green to-primary-shade-green h-full min-h-[24px]"></div>
            </div>
          </div>

          {steps.map((step, index) => (
            <div key={index} className="mb-4 last:mb-0">
              <MobileStep step={step} index={index} />
              <DesktopStep step={step} index={index} />
            </div>
          ))}
        </div>

        {isScrollable && showBottomGradient && (
          <div className="absolute bottom-0 left-0 right-0 h-8 gradient-mask-t-[rgba(0,0,0,1.0)_40%,rgba(0,0,0,0.5)_70%] pointer-events-none bg-black bg-opacity-50 rounded-b-xl"></div>
        )}
      </div>
    </section>
  );
};

export default ProcessStep;
